import React, { useState, ChangeEvent } from "react";
import { useAuthContext } from "auth/AuthProvider";
import { PasswordsState, ProfileState, ValidationErrors } from "./types";
import { validateEmail, validateFullName, validatePassword } from "./utils";
import { FormInput } from "components/fields/FormInput";
import Card from "components/card";
import banner from "assets/img/profile/banner.png";
import avatar from "assets/img/avatars/avatarSimmmple.png";
import api from "api/axios";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

export const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<ProfileState>({
    email: user.email,
    name: user.name,
    photoUrl: user.image,
  });
  const [newAvatar, setNewAvatar] = useState<File | null>(null);

  // Password state
  const [passwords, setPasswords] = useState<PasswordsState>({
    current: "",
    new: "",
    confirm: "",
  });

  // Loading states
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  // Validation errors
  const [profileErrors, setProfileErrors] = useState<ValidationErrors>({});
  const [passwordErrors, setPasswordErrors] = useState<ValidationErrors>({});

  // Track if fields have been touched
  const [isTouched, setIsTouched] = useState({
    name: false,
  });

  const handleProfileChange = (field: keyof ProfileState, value: string) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Mark field as touched
    setIsTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    if (profileErrors[field as keyof ValidationErrors]) {
      setProfileErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handlePasswordChange = (field: keyof PasswordsState, value: string) => {
    setPasswords((prev) => ({
      ...prev,
      [field]: value,
    }));

    const errorField =
      field === "current"
        ? "currentPassword"
        : field === "new"
        ? "newPassword"
        : "confirmPassword";
    if (passwordErrors[errorField as keyof ValidationErrors]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [errorField]: undefined,
      }));
    }
  };

  const handlePhotoChange = async (file: File) => {
    setNewAvatar(file);
  };

  const handleProfileSave = async () => {
    const emailError = validateEmail(profile.email);
    const nameError = validateFullName(profile.name);

    const errors: ValidationErrors = {
      name: nameError,
    };

    setProfileErrors(errors);

    // Check if there are any errors
    if (emailError || nameError) {
      return;
    }

    setIsProfileLoading(true);
    try {
      await api.put(`/users/profile`, {
        name: profile.name,
      });
      toast.success(t("profile.profileUpdated"));
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    // Validate passwords
    const currentError = validatePassword(passwords.current);
    const newError = validatePassword(passwords.new);
    const confirmError =
      passwords.new !== passwords.confirm ? t("profile.passwordsDontMatch") : undefined;

    const errors: ValidationErrors = {
      currentPassword: currentError,
      newPassword: newError,
      confirmPassword: confirmError,
    };

    setPasswordErrors(errors);

    // Check if there are any errors
    if (currentError || newError || confirmError) {
      return;
    }

    setIsPasswordLoading(true);
    try {
      await api.put(`/users/password`, {
        current_password: passwords.current,
        new_password: passwords.new,
      });
      setPasswords({ current: "", new: "", confirm: "" });
      toast.success(t("profile.passwordChanged"));
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <div className="mt-3 grid h-full w-full grid-cols-1 gap-5 lg:grid-cols-2">
      {/* Left column: Banner, Avatar, Profile Info */}
      <div className="rounded-[20px]">
        {/* Banner and Avatar */}
        <Card extra={"items-center pt-[16px] pb-10 px-[16px] bg-cover"}>
          <div
            className="relative mt-1 flex h-28 w-full justify-center rounded-[20px] bg-cover"
            style={{ backgroundImage: `url(${banner})` }}
          >
            <div className="absolute -bottom-12 flex h-[87px] w-[87px] items-center justify-center rounded-full bg-pink-400">
              <img
                className="h-full w-full rounded-full object-cover"
                src={profile.photoUrl || avatar}
                alt="Profile"
              />
            </div>
          </div>
          {/* Name and Account Type */}
          <div className="mt-14 flex flex-col items-center">
            <h4 className="mt-1 max-w-[20ch] truncate text-xl font-bold text-navy-700 dark:text-white">
              {profile.name?.length > 0 ? profile.name : t("common.user")}
            </h4>
            {/* <div className="mt-2 flex items-center justify-center">
              <h6 className="mt-2 text-sm font-normal text-gray-600 dark:text-gray-300">
                Account type
              </h6>
              <select
                className="mt-2 px-2 text-sm font-bold text-navy-700 dark:!bg-navy-800 dark:text-white"
                name="accountType"
                id="accountType"
              >
                <option value="user">User</option>
                <option value="admin">Administrator</option>
                <option value="moderator">Moderator</option>
              </select>
            </div> */}
          </div>
        </Card>
        {/* Profile Form */}
        <div className="mt-3">
          <Card extra={"w-full mt-3 px-6 py-6"}>
            <div className="w-full px-[8px]">
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {t("profile.accountSettings")}
              </h4>
              <p className="mt-1 text-base text-gray-600 dark:text-gray-300">
                {t("profile.changeUserInfo")}
              </p>
            </div>
            <div className="mt-[37px] grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormInput
                label={t("profile.userName")}
                placeholder={t("profile.userNamePlaceholder")}
                type="text"
                value={profile.name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleProfileChange("name", e.target.value)
                }
                required
                error={profileErrors.name}
              />
              <FormInput
                label={t("profile.emailAddress")}
                placeholder={t("profile.emailPlaceholder")}
                type="text"
                value={profile.email}
                onChange={() => {}}
                required
                disabled
              />
            </div>
            {/* Full width input example (future extensibility) */}
            {/* <InputField
              extra="mb-3"
              label="Job"
              placeholder="Web Developer"
              id="job"
              type="text"
            /> */}
            {/* <TextField
              extra="mb-4"
              label="About Me"
              placeholder="Tell something about yourself in 150 characters!"
              id="textarea"
              cols={30}
              rows={5}
            /> */}
            <div className="mt-4 flex w-full justify-end">
              <button
                onClick={handleProfileSave}
                disabled={
                  isProfileLoading || !isTouched.name || !!profileErrors.name
                }
                className={`linear rounded-xl px-8 py-2 text-base font-medium transition duration-200
                  ${
                    isProfileLoading || !isTouched.name || !!profileErrors.name
                      ? "cursor-not-allowed bg-gray-300 text-gray-400 opacity-60 dark:bg-navy-700 dark:text-gray-500"
                      : "bg-brand-500 text-white hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                  }`}
                style={{
                  pointerEvents:
                    isProfileLoading || !isTouched.name || !!profileErrors.name
                      ? "none"
                      : undefined,
                  filter:
                    isProfileLoading || !isTouched.name || !!profileErrors.name
                      ? "grayscale(0.7) brightness(0.95)"
                      : undefined,
                }}
                aria-disabled={
                  isProfileLoading || !isTouched.name || !!profileErrors.name
                }
                tabIndex={
                  isProfileLoading || !isTouched.name || !!profileErrors.name
                    ? -1
                    : 0
                }
              >
                {isProfileLoading ? t("profile.saving") : t("common.saveChanges")}
              </button>
            </div>
          </Card>
        </div>
      </div>
      {/* Right column: Password Form (and Social Profile for future extensibility) */}
      <div>
        {/* Social Profile (future extensibility) */}
        {/* <Card extra="w-full px-6 py-6">
          <div className="w-full px-[8px]">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Social Profile
            </h4>
            <p className="mt-1 text-base text-gray-600 dark:text-gray-300">
              Here you can set user social profiles
            </p>
          </div>
          <div className="mt-11 grid grid-cols-1 gap-3">
            <InputField label="Twitter Username" placeholder="Twitter Username" id="twitter" type="text" />
            <InputField label="Facebook Username" placeholder="Facebook Username" id="facebook" type="text" />
            <InputField label="Github Username" placeholder="Github Username" id="github" type="text" />
            <div className="mt-3 flex w-full justify-end">
              <button className="linear rounded-xl bg-brand-500 px-8 py-2 text-base font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200">
                Save changes
              </button>
            </div>
          </div>
        </Card> */}
        {/* Password Section */}
        <Card extra={"w-full mt-3 px-6 py-6"}>
          <div className="w-full px-2">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {t("profile.changePassword")}
            </h4>
            <p className="mt-1 text-base text-gray-600 dark:text-gray-300">
              {t("profile.setNewPassword")}
            </p>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-3">
            <FormInput
              label={t("profile.oldPassword")}
              placeholder={t("profile.oldPasswordPlaceholder")}
              type="password"
              value={passwords.current}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handlePasswordChange("current", e.target.value)
              }
              required
              error={passwordErrors.currentPassword}
            />
            <FormInput
              label={t("profile.newPassword")}
              placeholder={t("profile.newPasswordPlaceholder")}
              type="password"
              value={passwords.new}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handlePasswordChange("new", e.target.value)
              }
              required
              error={passwordErrors.newPassword}
            />
            <FormInput
              label={t("profile.newPasswordConfirmation")}
              placeholder={t("profile.confirmNewPassword")}
              type="password"
              value={passwords.confirm}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handlePasswordChange("confirm", e.target.value)
              }
              required
              error={passwordErrors.confirmPassword}
            />
            <div className="mt-2 flex w-full justify-end">
              <button
                onClick={handlePasswordSave}
                disabled={
                  isPasswordLoading ||
                  !!passwordErrors.currentPassword ||
                  !!passwordErrors.newPassword ||
                  !!passwordErrors.confirmPassword
                }
                className={`linear rounded-xl px-8 py-2 text-base font-medium transition duration-200
                  ${
                    isPasswordLoading ||
                    !!passwordErrors.currentPassword ||
                    !!passwordErrors.newPassword ||
                    !!passwordErrors.confirmPassword
                      ? "cursor-not-allowed bg-gray-300 text-gray-400 opacity-60 dark:bg-gray-700 dark:text-gray-500"
                      : "bg-brand-500 text-white hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                  }`}
                tabIndex={
                  isPasswordLoading ||
                  !!passwordErrors.currentPassword ||
                  !!passwordErrors.newPassword ||
                  !!passwordErrors.confirmPassword
                    ? -1
                    : 0
                }
                aria-disabled={
                  isPasswordLoading ||
                  !!passwordErrors.currentPassword ||
                  !!passwordErrors.newPassword ||
                  !!passwordErrors.confirmPassword
                    ? "true"
                    : "false"
                }
                style={{
                  pointerEvents:
                    isPasswordLoading ||
                    !!passwordErrors.currentPassword ||
                    !!passwordErrors.newPassword ||
                    !!passwordErrors.confirmPassword
                      ? "none"
                      : undefined,
                }}
              >
                {isPasswordLoading ? t("profile.changing") : t("profile.changePassword")}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
